import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Package, Truck } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { EASE, Reveal, money } from "../components/bits";
import SEO from "../components/SEO";

const METHOD_LABELS = { card: "Card · Stripe", paypal: "PayPal" };

export default function Confirmation() {
  const { orderId } = useParams();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clear } = useCart();
  const [order, setOrder] = useState(state?.order || null);
  const [paid, setPaid] = useState(!sessionId);
  const [timedOut, setTimedOut] = useState(false);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaid(true);
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          clearInterval(poll);
          const o = await api.get(`/orders/${orderId}`);
          setOrder(o.data);
        }
      } catch {}
      if (tries >= 15) {
        clearInterval(poll);
        setTimedOut(true);
      }
    }, 2000);
    return () => clearInterval(poll);
  }, [sessionId, orderId, clear]);

  useEffect(() => {
    if (!order) {
      api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
    }
  }, [orderId, order]);

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center font-mono2 text-xs uppercase tracking-[0.3em] text-[#4ADE80]">
        Fetching your order…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 pb-28 pt-32 sm:px-8" data-testid="order-confirmation">
      <SEO title="Order Confirmation — SuperTechLED" noindex />
      <div className="relative text-center">
        <motion.img
          src="/img/brand/alien.png"
          alt="SuperTechLED alien mascot"
          initial={{ opacity: 0, x: 40, rotate: 6 }}
          animate={paid ? { opacity: 1, x: 0, rotate: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
          className="animate-float-slow pointer-events-none absolute -top-24 right-0 hidden h-36 w-auto drop-shadow-[0_0_36px_rgba(57,255,20,0.35)] lg:block"
        />
        {paid ? (
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#39FF14] shadow-[0_0_60px_rgba(57,255,20,0.5)]"
          >
            <Check className="h-10 w-10 text-[#050B07]" strokeWidth={3} />
          </motion.div>
        ) : (
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#39FF14]/40">
            <Loader2 className="h-9 w-9 animate-spin text-[#39FF14]" />
          </div>
        )}
        <Reveal delay={0.15}>
          <h1 className="font-display mt-8 text-3xl font-black tracking-tight text-[#F0FDF4] sm:text-5xl" data-testid="confirmation-headline">
            {paid ? (
              <>Order locked in<span className="text-[#39FF14]">.</span></>
            ) : timedOut ? (
              "Payment processing…"
            ) : (
              "Confirming your payment…"
            )}
          </h1>
          <p className="mx-auto mt-4 max-w-md text-sm text-[#9FB8A8] sm:text-base">
            {paid ? (
              <>
                Thanks {order.customer.name.split(" ")[0]} — your lights are being picked and packed. A confirmation email
                is on its way to <span className="text-[#86EFAC]">{order.customer.email}</span>.
              </>
            ) : timedOut ? (
              "Stripe is still finalising your payment. You'll get an email the moment it clears — your order is safe."
            ) : (
              "Hang tight — we're confirming your card payment with Stripe."
            )}
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.25}>
        <div className="glass mt-10 rounded-2xl p-6 sm:p-8">
          <div className="grid gap-6 sm:grid-cols-3">
            <div>
              <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]">Order</div>
              <div className="mt-1 font-display text-lg font-bold text-[#F0FDF4]" data-testid="order-id">{order.order_id}</div>
            </div>
            <div>
              <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]">Tracking</div>
              <div className="mt-1 font-mono2 text-sm text-[#86EFAC]" data-testid="tracking-code">{order.tracking_code}</div>
            </div>
            <div>
              <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]">Delivery</div>
              <div className="mt-1 flex items-center gap-1.5 text-sm text-[#F0FDF4]">
                <Truck className="h-4 w-4 text-[#39FF14]" /> 2–4 business days
              </div>
            </div>
          </div>
          <div className="mt-6 space-y-3 border-t border-[#39FF14]/10 pt-5">
            {order.items.map((i) => (
              <div key={i.sku} className="flex items-center gap-3">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-[#050B07]">
                  <img src={i.image} alt={i.name} className="h-full w-full object-contain p-1" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#F0FDF4]">{i.name}</div>
                  <div className="font-mono2 text-[10px] uppercase tracking-widest text-[#4ADE80]">{i.variant_name} × {i.qty}</div>
                </div>
                <div className="text-sm font-bold text-[#F0FDF4]">{money(i.price * i.qty)}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-[#39FF14]/10 pt-4">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">
              {paid ? `Paid via ${METHOD_LABELS[order.payment_method] || order.payment_method}` : "Awaiting payment"}
            </span>
            <span className="font-display text-2xl font-black text-[#F0FDF4]" data-testid="order-total">{money(order.total)}</span>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.35} className="mt-8 flex flex-col items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-[#9FB8A8]">
          <Package className="h-4 w-4 text-[#39FF14]" />
          Free express shipping to {order.customer.city}, {order.customer.state} {order.customer.postcode}
        </div>
        <Link
          to="/shop"
          data-testid="confirmation-shop-button"
          className="group inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-8 py-4 font-display text-sm font-bold text-[#050B07] transition-shadow hover:shadow-[0_0_36px_rgba(57,255,20,0.45)]"
        >
          Back to the range
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  );
}
